import { Info } from 'lucide-react';
import { cx } from '../styles/theme';

/**
 * A focusable "ⓘ" affordance that carries supplementary help text accessibly.
 *
 * It replaces the `cursor-help` + `title`-only pattern that several views used
 * on chart/section headings: a bare `title` is a mouse-hover tooltip only —
 * invisible to keyboard and screen-reader users (see the
 * dashboard-accessibility-conventions topic, rule #2). Here the same string is
 * exposed via `aria-label` (screen readers) AND `title` (mouse hover), and the
 * trigger is a real <button> so it is Tab-reachable with the shared
 * focus-visible ring. Keep it a sibling of the heading (not a child) so the
 * heading's own accessible name stays the short label, not the whole paragraph.
 */
export function InfoHint({ label, className }: { label: string; className?: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex items-center rounded-full text-navy-400 dark:text-navy-500',
        'hover:text-navy-600 dark:hover:text-navy-300 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400',
        className,
      )}
    >
      <Info className="w-3.5 h-3.5" aria-hidden="true" />
    </button>
  );
}
